import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../../../../css/Recruitment/Sections.css";
import YesNoRadio from "../RadioButton";
import Checkbox from "../../../../DesignSystem/Inputs/Checkbox";
import { readDataS2 } from "../../../../../backend/Recruitment/Sections/Section2/readDataS2";
import { writeDataS2 } from "../../../../../backend/Recruitment/Sections/Section2/writeDataS2";
import errorTrackerV2 from "../Validation/Utils/ErrorTrackerV2";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "../../Utils/submissionListener";
import { auth } from "../../../../../backend/firebaseConfig";
import "../../../../../css/DesignSystem/Sections/Section2.css";

//  pure validator
import { validateSection2 } from "../Validation/section2";
import InputField from "../../../../DesignSystem/Inputs/InputField";
import AlertBanner from "../../../../DesignSystem/AlertBanner/AlertBanner";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";

const initialState = {
  FPC1: null,
  FPC2: null,
  FPC3: null,
  CF1: null,
  CF2: null,
  CF3: null,
  CF4: null,
  CF5: null,
  CF6: null,
  MAQ: null,
  CeMAP1: null,
  CeMAP2: null,
  CeMAP3: null,
  CeMAP_Bridge: null,
  CeFA1: null,
  CeFA2: null,
  CeFA3: null,
  CeFA4: null,
  IFS_DIPLOMA: null,
  JO1: null,
  JO2: null,
  JO3: null,
  JO4: null,
  JO5: null,
  JO6: null,
  JO7: null,
  JO8: null,
  JO9: null,
  JO10: null,
  JO11: null,
  JO12: null,
  RO1: null,
  RO2: null,
  RO3: null,
  RO4: null,
  RO5: null,
  RO6: null,
  RO7: null,
  RO8: null,
  CII_DIPLOMA: null,
  Equity_Release: null,
  Long_Term_Care: null,
  Qualification1: "",
  Qualification2: "",
  Qualification3: "",
};

const Section2 = ({ onValidationStatusChange }) => {
  const sectionTopRef = useRef(null);
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());
  const [qualificationsCount, setQualificationsCount] = useState(1);

  const ciiQualifications = useMemo(
    () => [
      "FPC1",
      "FPC2",
      "FPC3",
      "CF1",
      "CF2",
      "CF3",
      "CF4",
      "CF5",
      "CF6",
      "MAQ",
    ],
    [],
  );

  const libifQualifications = useMemo(
    () => [
      "CeMAP1",
      "CeMAP2",
      "CeMAP3",
      "CeMAP_Bridge",
      "CeFA1",
      "CeFA2",
      "CeFA3",
      "CeFA4",
      "IFS_DIPLOMA",
    ],
    [],
  );

  const joQualifications = useMemo(
    () => [
      "JO1",
      "JO2",
      "JO3",
      "JO4",
      "JO5",
      "JO6",
      "JO7",
      "JO8",
      "JO9",
      "JO10",
      "JO11",
      "JO12",
    ],
    [],
  );

  const roQualifications = useMemo(
    () => ["RO1", "RO2", "RO3", "RO4", "RO5", "RO6", "RO7", "RO8"],
    [],
  );

  //  submission listener (fix dependency bug: do NOT call getIsSubmitted() in deps)
  useEffect(() => {
    const remove = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => remove();
  }, []);

  //  load once
  useEffect(() => {
    (async () => {
      const data = await readDataS2();
      const normaliseBool = (v) =>
        v === "true" ? true : v === "false" ? false : v;

      if (!data) return;

      setFormData((prev) => {
        const merged = { ...prev, ...data };
        // normalise known yes/no fields + any qualification booleans
        for (const k of Object.keys(initialState)) {
          merged[k] = normaliseBool(merged[k]);
        }
        return merged;
      });
    })();
  }, []);

  //  one validation function (pure + sync tracker)
  const runValidation = (nextData) => {
    const formErrors = validateSection2(nextData);

    setErrors(formErrors);
    errorTrackerV2.setSectionErrors("section2", formErrors);

    const ok = Object.keys(formErrors).length === 0;
    onValidationStatusChange?.(ok);

    return ok;
  };

  //  validate on change (live)
  useEffect(() => {
    runValidation(formData);
  }, [formData]); // intentionally only formData

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    // If YesNoRadio sends "true"/"false" strings, normalise:
    let nextValue = value;
    if (value === "true") nextValue = true;
    if (value === "false") nextValue = false;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleAddQualification = () => {
    if (qualificationsCount < 3) {
      setQualificationsCount((prev) => prev + 1);
    }
  };

  const renderQualificationFields = () => {
    const fields = [];
    for (let i = 1; i <= qualificationsCount; i++) {
      const fieldName = `Qualification${i}`;
      fields.push(
        <div className="input-group-row" key={fieldName}>
          <div className="Content">Qualification {i}</div>
          <div className="input-group">
            <InputField
              name={fieldName}
              isTextArea
              placeholder="Qualification Details"
              value={formData[fieldName]}
              onChange={handleChange}
              multiline
              rows={2}
            />
          </div>
        </div>,
      );
    }
    return fields;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    sectionTopRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowErrors(true);

    const valid = runValidation(formData);

    const filteredFormData = Object.fromEntries(
      Object.entries(formData).filter(([, v]) => v !== null),
    );

    // Save data regardless of validation status
    await writeDataS2(filteredFormData);

    // Show appropriate alert based on validation result
    if (valid ) {
      setAlertMessage("Data saved successfully!");
      setAlertType("success");
    } else {
      setAlertMessage(
        "Data saved with errors. Please review the fields marked with errors.",
      );
      setAlertType("alert");
    }
  };

  const renderQualifications = (qualifications) => (
    <div className="qualifications-grid">
      {qualifications.map((qualification) => (
        <Checkbox
          key={qualification}
          id={qualification}
          name={qualification}
          label={qualification}
          checked={formData[qualification] || false}
          onChange={handleChange}
        />
      ))}
    </div>
  );
  const isValid = Object.keys(errors).length === 0;

  return (
    <form className="section-form" onSubmit={handleSubmit} ref={sectionTopRef}>
      {alertMessage && (
        <AlertBanner
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertMessage("")}
        />
      )}
      <h3>Section 2 - Professional Qualifications</h3>
      <div className="section2-container">
        <div className="section2-column-container">
          <div className="Content">2.1 Professional Standing</div>
          <div className="Content">
            Please select the boxes that correspond to the qualifications you
            have attained. Copies of the relevant exam certificates must be
            provided to us. If you are already a financial planner, please also
            submit your Statement of Professional Standing.
          </div>
        </div>
        <div className="section2-column-container">
          <div className="Content">CII</div>
          {renderQualifications(ciiQualifications)}
        </div>

        <div className="section2-column-container">
          <div className="Content">Walbrook Institute London (LIBF/IFS)</div>
          {renderQualifications(libifQualifications)}
        </div>

        <div className="Content">CII JO</div>
        {renderQualifications(joQualifications)}

        <div className="Content">CII RO</div>
        {renderQualifications(roQualifications)}

        <div className="Content">CII DIPLOMA</div>
        {renderQualifications(["CII_DIPLOMA"])}
      </div>
      <div className="separator" />
      <div className="input-group-row">
        <div className="Content">
          Do you have any Equity Release qualifications?
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="Equity_Release"
            value={formData.Equity_Release}
            onChange={handleChange}
            error={errors.Equity_Release}
            na={false}
          />
        </div>
      </div>

      <div className="input-group-row">
        <div className="Content">
          Do you have Long Term Care qualifications?
          <span style={{ color: "var(--Error)" }}>*</span>
        </div>
        <div className="input-group">
          <YesNoRadio
            name="Long_Term_Care"
            value={formData.Long_Term_Care}
            onChange={handleChange}
            error={errors.Long_Term_Care}
            na={false}
          />
        </div>
      </div>
      <div className="separator" />

      <div className="input-group-row">
        <div className="Content">
          If you do not hold any financial services qualifications, please
          provide details of any higher-level qualifications you have obtained
          (e.g., degrees, professional certifications, or other relevant
          qualifications).
        </div>
        {qualificationsCount < 3 && (
          <button
            type="button"
            className="add-qualification-btn"
            onClick={handleAddQualification}
          >
            <span className="plus-icon">+</span> Add Qualification
          </button>
        )}
      </div>

      {/* If you add qualificationDetails rule back, show it like this: */}
      {/* <span className="error-message">{errors.qualificationDetails}</span> */}

      {renderQualificationFields()}

      {!isSubmitted && (
        <PrimaryButton
          label="SAVE"
          onClick={handleSubmit}
          // disabled={!bypass && !isValid}
        />
      )}
    </form>
  );
};

export default Section2;
